import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect } from 'react';
import URI from 'urijs';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { getKeyValue, getListValue, t } from 'Src/i18n';
import { TRACK_PAGE_NAMES } from 'Tracking/constants';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import type { AnimationEvent } from 'Components/widgets/event-animation/EventAnimation';
import { EventAnimation } from 'Components/widgets/event-animation/EventAnimation';
import { GreatMembersExclusivity } from 'Components/widgets/great-members-exclusivity/GreatMembersExclusivity';
import { GreatMembersIntroduction } from 'Components/widgets/great-members-introduction/GreatMembersIntroduction';
import { GreatMembersPointsSection } from 'Components/widgets/great-members-points-section/GreatMembersPointsSection';
import { GreatMembersStasusesSection } from 'Components/widgets/great-members-stasuses-section/GreatMembersStasusesSection';
import { Hero } from 'Components/widgets/hero/Hero';
import { Breadcrumb } from 'Components/frameworks/breadcrumb/Breadcrumb';
import { urlPattern } from 'Routes/routeBuilder';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
type Props = {
  animation: Array<AnimationEvent>;
  loyaltyProgram: Array<LoyaltyProgram>;
  loyaltyProgramSimulation: LoyaltyProgramSimulation;
  profile: Profile;
};
export const GreatMembersView = ({
  animation = [],
  loyaltyProgram,
  loyaltyProgramSimulation,
  profile,
}: Props) => {
  const { asPath } = useRouter();
  const hash = new URI(asPath).hash();
  useEffect(() => {
    // unfortunetely we do not get the hash on no spa navigation don't know why
    if (hash || window.location.hash) {
      setTimeout(() => {
        scrollToElement(hash || window.location.hash);
      }, 600);
    }
  }, [hash]);
  const pageTitle = t('customerAccount.greatMember.pageTitle');
  const pageDescription = t('customerAccount.greatMember.pageDescription');
  const loyaltyStatus = profile?.loyaltyProgram?.status || hash.substring(1, hash.length);
  const { isQuickLogged, isLogged } = useAuthentication();
  return (
    <Fragment>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta name="description" key="description" content={pageDescription} />
      </Head>
      <TrackPage pageName={TRACK_PAGE_NAMES.CA_GREAT_MEMBERS} />
      <header className="Line Line-Immersive" role="banner">
        <Hero
          title={(getListValue('customerAccount.greatMember.titles') || []).join(' ')}
          url={t('customerAccount.greatMember.imageUrl')}
        />
      </header>
      <main className="space-y-80 pb-100 pt-40 sm:space-y-120" role="main">
        {(isLogged || isQuickLogged) && (
          <SiteContainer>
            <Breadcrumb
              items={[
                {
                  label: isQuickLogged
                    ? t('account.authenticated.dashboard')
                    : t('customerAccount.home.pageTitle'),
                  url: urlPattern('account'),
                },
                {
                  label: t('customerAccount.greatMember.pageTitle'),
                },
              ]}
            />
          </SiteContainer>
        )}

        {animation.length > 0 && (
          <SiteContainer id="animation">
            <Title className="mb-40" hLevel={2} theme="title-brand-n0">
              {t('customerAccount.greatMember.animation.title')}
            </Title>
            <EventAnimation events={animation} />
          </SiteContainer>
        )}

        {profile?.loyaltyProgram &&
          getKeyValue('customerAccount.greatMember.introduction.enabled') && (
            <SiteContainer id="introduction">
              <GreatMembersIntroduction
                loyaltyProgram={loyaltyProgram}
                profile={profile.loyaltyProgram}
              />
            </SiteContainer>
          )}

        {getKeyValue('customerAccount.greatMember.stepsSection.enabled') && (
          <SiteContainer id="exclusivity">
            <GreatMembersExclusivity />
          </SiteContainer>
        )}

        {getKeyValue('customerAccount.greatMember.pointsSection.enabled') && (
          <SiteContainer id="points">
            <GreatMembersPointsSection loyaltyProgramSimulation={loyaltyProgramSimulation} />
          </SiteContainer>
        )}

        {getKeyValue('customerAccount.greatMember.stasusesSection.enabled') && (
          <GreatMembersStasusesSection
            loyaltyProgram={loyaltyProgram}
            loyaltyStatus={loyaltyStatus}
          />
        )}
      </main>
    </Fragment>
  );
};
